import { useState } from 'react';
import { api } from '../../shared/api/client';
import { Card, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney, monthStart } from './money';

type Line = { kind: 'persona' | 'familia'; id: string; name: string; diezmosCents: number; ofrendasCents: number; totalCents: number; count: number };
type Giving = { from: string; to: string; people: Line[]; families: Line[]; anonymous: Omit<Line, 'kind' | 'id' | 'name'>; totalCents: number };

/** Sin acentos ni mayúsculas, para buscar «jose» y encontrar «José». */
const fold = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** Estadística del pastor y la administración: diezmos y ofrendas por persona y por familia. A los demás no se les muestra. */
export function GivingCard({ churchId, today, version }: { churchId: string; today: string; version: number }) {
  const [from, setFrom] = useState(monthStart(today));
  const [to, setTo] = useState(today);
  const [search, setSearch] = useState('');
  const data = useAsync((signal) => api.get<Giving>(`/finance/churches/${churchId}/giving?from=${from}&to=${to}`, signal).catch(() => null), [churchId, from, to, version]);
  if (data.status === 'ready' && !data.data) return null;
  const money = (c: number) => formatMoney(c, 'MXN');
  const needle = fold(search.trim());
  const match = (lines: Line[]) => (needle ? lines.filter((l) => fold(l.name).includes(needle)) : lines);
  const table = (title: string, all: Line[]) => {
    const lines = match(all);
    return lines.length ? (
      <>
        <h3 className="small">{title}</h3>
        <ul className="list">
          {lines.map((l) => (
            <li key={l.id} className="list__item list__item--actions">
              <span>
                {l.kind === 'familia' ? `Familia ${l.name}` : l.name}
                <span className="muted small">
                  {' '}
                  · Diezmos {money(l.diezmosCents)} · Ofrendas {money(l.ofrendasCents)}
                </span>
              </span>
              <span className="money">{money(l.totalCents)}</span>
            </li>
          ))}
        </ul>
      </>
    ) : null;
  };
  return (
    <Card title="Diezmos y ofrendas por persona y familia">
      <p className="muted small">Solo lo ven el pastor y la administración.</p>
      <div className="form__row">
        <div className="field">
          <label htmlFor="giving-from">Desde</label>
          <input id="giving-from" type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="giving-to">Hasta</label>
          <input id="giving-to" type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="giving-search">Buscar por nombre</label>
        <input id="giving-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nombre de la persona o familia" />
      </div>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'ready' && data.data ? (
        <>
          {table('Por familia', data.data.families)}
          {table('Por persona', data.data.people)}
          {data.data.anonymous.totalCents ? (
            <p className="small">
              Sin nombre: <span className="money">{money(data.data.anonymous.totalCents)}</span>
            </p>
          ) : null}
          {!data.data.families.length && !data.data.people.length ? <p className="muted">No hay diezmos ni ofrendas a nombre de alguien en estas fechas.</p> : null}
          {needle && (data.data.families.length || data.data.people.length) && !match(data.data.families).length && !match(data.data.people).length ? <p className="muted">Nadie con ese nombre en estas fechas.</p> : null}
          <p className="small">
            <strong>Total:</strong> <span className="money">{money(data.data.totalCents)}</span>
          </p>
        </>
      ) : null}
    </Card>
  );
}
